<template>
  <div class="templates-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="templates-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-left">
              <h1 class="page-title">Message Templates</h1>
              <p class="page-subtitle">Create and manage WhatsApp message templates</p>
            </div>
            <div class="header-actions">
              <button @click="syncTemplates" class="sync-btn" :disabled="syncing">
                <div v-if="syncing" class="loading-spinner-small"></div>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                </svg>
                <span>Sync Templates</span>
              </button>
              <button @click="openCreateModal" class="create-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                <span>Create Template</span>
              </button>
            </div>
          </div>

          <!-- Filters Section -->
          <div class="filters-section">
            <div class="search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="search-icon">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
              </svg>
              <input
                v-model="searchQuery"
                @input="handleSearch"
                type="text"
                placeholder="Search templates by name..."
                class="search-input"
              />
            </div>

            <div class="filter-controls">
              <select v-model="filters.status" @change="loadTemplates" class="filter-select">
                <option value="">All Statuses</option>
                <option value="APPROVED">Approved</option>
                <option value="PENDING">Pending</option>
                <option value="REJECTED">Rejected</option>
                <option value="PAUSED">Paused</option>
                <option value="DISABLED">Disabled</option>
              </select>

              <select v-model="filters.category" @change="loadTemplates" class="filter-select">
                <option value="">All Categories</option>
                <option value="MARKETING">Marketing</option>
                <option value="UTILITY">Utility</option>
                <option value="AUTHENTICATION">Authentication</option>
              </select>

              <select v-model="filters.language" @change="loadTemplates" class="filter-select">
                <option value="">All</option>
                <option value="en">English</option>
                <option value="ar">Arabic</option>
              </select>
            </div>
          </div>

          <!-- Templates Grid -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading templates...</p>
          </div>

          <div v-else-if="templates.length === 0" class="empty-state">
            <div class="empty-icon">📋</div>
            <h3>No templates found</h3>
            <p v-if="searchQuery || filters.status || filters.category">
              No templates match your current filters
            </p>
            <p v-else>Create your first template to get started</p>
            <button v-if="!searchQuery && !filters.status && !filters.category" @click="openCreateModal" class="create-btn-empty">
              Create Template
            </button>
          </div>

          <div v-else class="templates-grid">
            <div
              v-for="template in templates"
              :key="template.id"
              class="template-card"
            >
              <!-- Template Info Header -->
              <div class="template-info-header">
                <div class="template-name-section">
                  <h3 class="template-name">{{ template.name }}</h3>
                  <span class="template-language">{{ getLanguageName(template.language) }}</span>
                </div>
                <div class="template-badges">
                  <span class="status-badge" :class="'status-' + template.status.toLowerCase()">
                    {{ template.status }}
                  </span>
                  <span class="category-badge" :class="'category-' + template.category.toLowerCase()">
                    {{ template.category }}
                  </span>
                </div>
              </div>

              <!-- WhatsApp Message Bubble -->
              <div class="whatsapp-message-bubble">
                <div v-for="(component, index) in template.components" :key="index" class="message-component">
                  <!-- Header Component -->
                  <div v-if="component.type === 'HEADER'" class="message-header">
                    <strong v-if="component.format === 'TEXT'" v-html="highlightVariables(component.text)"></strong>
                    <div v-else-if="component.format === 'IMAGE'" class="message-media">
                      <img src="https://api.quali-d.com/image_placeholder.jpg" alt="Image" class="media-image-preview" />
                    </div>
                    <div v-else-if="component.format === 'VIDEO'" class="message-media">
                      <img src="https://api.quali-d.com/video_placeholder.jpg" alt="Video" class="media-image-preview" />
                    </div>
                    <div v-else-if="component.format === 'DOCUMENT'" class="message-media">
                      <div class="document-placeholder">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" style="color: #667781;">
                          <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                        </svg>
                        <span>Document</span>
                      </div>
                    </div>
                  </div>

                  <!-- Body Component -->
                  <div v-else-if="component.type === 'BODY'" class="message-body" v-html="highlightVariables(component.text)"></div>

                  <!-- Footer Component -->
                  <div v-else-if="component.type === 'FOOTER'" class="message-footer">
                    {{ component.text }}
                  </div>

                  <!-- Buttons Component -->
                  <div v-else-if="component.type === 'BUTTONS'" class="message-buttons">
                    <button v-for="(btn, btnIdx) in component.buttons" :key="btnIdx" class="message-button">
                      <svg v-if="btn.type === 'URL'" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                      </svg>
                      <svg v-else-if="btn.type === 'PHONE_NUMBER'" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
                      </svg>
                      {{ btn.text }}
                    </button>
                  </div>
                </div>

                <!-- WhatsApp timestamp -->
                <div class="message-timestamp">
                  <span>{{ formatTemplateTime() }}</span>
                  <svg width="16" height="16" viewBox="0 0 16 15" fill="none">
                    <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.541l1.32 1.266c.143.14.361.125.484-.033l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L1.891 7.769a.366.366 0 0 0-.515.006l-.423.433a.364.364 0 0 0 .006.514l3.258 3.185c.143.14.361.125.484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z" fill="currentColor"/>
                  </svg>
                </div>
              </div>

              <!-- Quality & Rejection Info -->
              <div v-if="template.quality_indicator || template.rejected_reason" class="template-status-info">
                <!-- Quality Rating Badge -->
                <div v-if="template.quality_indicator" class="quality-rating-badge" :class="'quality-' + template.quality_indicator.color">
                  <div class="quality-icon">
                    <!-- High Quality Icon -->
                    <svg v-if="template.quality_indicator.icon === 'check-circle'" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <!-- Medium/Warning Icon -->
                    <svg v-else-if="template.quality_indicator.icon === 'alert-circle'" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                    </svg>
                    <!-- Low Quality Icon -->
                    <svg v-else-if="template.quality_indicator.icon === 'x-circle'" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>
                    </svg>
                    <!-- Flagged Icon -->
                    <svg v-else-if="template.quality_indicator.icon === 'alert-triangle'" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                    </svg>
                    <!-- Pending/Clock Icon -->
                    <svg v-else-if="template.quality_indicator.icon === 'clock'" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                    </svg>
                    <!-- Unknown/Help Icon -->
                    <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
                    </svg>
                  </div>
                  <div class="quality-content">
                    <span class="quality-label">{{ template.quality_indicator.label }}</span>
                    <span class="quality-description">{{ template.quality_indicator.description }}</span>
                  </div>
                </div>

                <!-- Rejection Info -->
                <div v-if="template.rejected_reason" class="rejection-info">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                  </svg>
                  <span>{{ template.rejected_reason }}</span>
                </div>
              </div>

              <!-- Actions -->
              <div class="template-actions">
                <button @click="viewTemplate(template)" class="action-btn view-btn" title="View Full Details">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                  </svg>
                  <span>View</span>
                </button>
                <button v-if="template.status === 'APPROVED'" @click="editTemplate(template)" class="action-btn edit-btn" title="Edit Template">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                  </svg>
                  <span>Edit</span>
                </button>
                <button @click="openDeleteModal(template)" class="action-btn delete-btn" title="Delete Template">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                  </svg>
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Template Detail Modal -->
    <div v-if="showDetailModal" class="modal-overlay" @click="closeDetailModal">
      <div class="modal-container template-detail-modal" @click.stop>
        <div class="modal-header">
          <h3>Template Details</h3>
          <button @click="closeDetailModal" class="modal-close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div class="modal-content" v-if="selectedTemplate">
          <div class="template-detail-header">
            <h2 class="template-detail-name">{{ selectedTemplate.name }}</h2>
            <div class="template-detail-badges">
              <span class="status-badge" :class="'status-' + selectedTemplate.status.toLowerCase()">
                {{ selectedTemplate.status }}
              </span>
              <span class="category-badge" :class="'category-' + selectedTemplate.category.toLowerCase()">
                {{ selectedTemplate.category }}
              </span>
              <span class="language-badge">{{ selectedTemplate.language }}</span>
            </div>
          </div>

          <div class="template-preview-section">
            <h4>Message Preview</h4>
            <div class="whatsapp-preview">
              <div v-for="(component, index) in selectedTemplate.components" :key="index" class="preview-component">
                <div v-if="component.type === 'HEADER'" class="preview-header">
                  <strong v-if="component.format === 'TEXT'">{{ component.text }}</strong>
                  <div v-else-if="component.format === 'IMAGE'" class="preview-media">
                    📷 Image Header
                  </div>
                  <div v-else-if="component.format === 'VIDEO'" class="preview-media">
                    🎥 Video Header
                  </div>
                  <div v-else-if="component.format === 'DOCUMENT'" class="preview-media">
                    📄 Document Header
                  </div>
                </div>
                <div v-else-if="component.type === 'BODY'" class="preview-body">
                  {{ component.text }}
                </div>
                <div v-else-if="component.type === 'FOOTER'" class="preview-footer">
                  {{ component.text }}
                </div>
                <div v-else-if="component.type === 'BUTTONS'" class="preview-buttons">
                  <button v-for="(btn, btnIdx) in component.buttons" :key="btnIdx" class="preview-button">
                    {{ btn.text }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Quality Rating Section -->
          <div v-if="selectedTemplate.quality_indicator" class="quality-section">
            <h4>Quality Rating</h4>
            <div class="quality-detail-card" :class="'quality-' + selectedTemplate.quality_indicator.color">
              <div class="quality-header">
                <div class="quality-icon-large">
                  <!-- High Quality Icon -->
                  <svg v-if="selectedTemplate.quality_indicator.icon === 'check-circle'" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                  <!-- Medium/Warning Icon -->
                  <svg v-else-if="selectedTemplate.quality_indicator.icon === 'alert-circle'" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  <!-- Low Quality Icon -->
                  <svg v-else-if="selectedTemplate.quality_indicator.icon === 'x-circle'" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>
                  </svg>
                  <!-- Flagged Icon -->
                  <svg v-else-if="selectedTemplate.quality_indicator.icon === 'alert-triangle'" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                  </svg>
                  <!-- Pending/Clock Icon -->
                  <svg v-else-if="selectedTemplate.quality_indicator.icon === 'clock'" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
                  </svg>
                  <!-- Unknown/Help Icon -->
                  <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
                  </svg>
                </div>
                <div class="quality-info-text">
                  <span class="quality-rating-label">{{ selectedTemplate.quality_indicator.label }}</span>
                  <span class="quality-rating-value">{{ selectedTemplate.quality_indicator.rating }}</span>
                </div>
              </div>
              <p class="quality-description-text">{{ selectedTemplate.quality_indicator.description }}</p>
            </div>
          </div>

          <div v-if="selectedTemplate.rejected_reason" class="rejection-section">
            <h4>Rejection Reason</h4>
            <div class="rejection-box">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
              </svg>
              <span>{{ selectedTemplate.rejected_reason }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
      <div class="modal-container delete-modal" @click.stop>
        <div class="modal-header">
          <h3>Delete Template</h3>
          <button @click="closeDeleteModal" class="modal-close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div class="modal-content">
          <div class="warning-message">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <p>Are you sure you want to delete this template?</p>
            <p class="warning-subtitle">This action cannot be undone.</p>
          </div>

          <div class="delete-form">
            <label for="deleteConfirmName">Type the template name to confirm:</label>
            <input
              id="deleteConfirmName"
              v-model="deleteConfirmName"
              type="text"
              :placeholder="templateToDelete?.name"
              class="delete-input"
            />
          </div>

          <div class="modal-actions">
            <button @click="closeDeleteModal" class="cancel-btn">Cancel</button>
            <button
              @click="confirmDelete"
              class="delete-confirm-btn"
              :disabled="deleting || deleteConfirmName !== templateToDelete?.name"
            >
              <div v-if="deleting" class="loading-spinner-small"></div>
              <span v-else>Delete Template</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Template Modal -->
    <div v-if="showTemplateModal" class="modal-overlay" @click="closeTemplateModal">
      <div class="modal-container template-builder-modal" @click.stop>
        <div class="modal-header">
          <h3>{{ isEditMode ? 'Edit Template' : 'Create New Template' }}</h3>
          <button @click="closeTemplateModal" class="modal-close-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div class="modal-content">
          <form @submit.prevent="submitTemplate" class="template-form">
            <div class="form-layout">
              <!-- Left Side: Form Fields -->
              <div class="form-fields">
                <!-- AI Generation Section (Only for Create mode) -->
                <div v-if="!isEditMode" class="form-section ai-section">
                  <div class="ai-header">
                    <div class="ai-header-left">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style="color: #9333ea;">
                        <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
                      </svg>
                      <h4 class="section-title" style="margin: 0; padding: 0; border: none;">AI Template Generator</h4>
                    </div>
                    <span class="ai-cost-badge">50 Credits</span>
                  </div>

                  <div v-if="!aiGenerated" class="ai-prompt-box">
                    <p class="ai-description">Describe what you want your template to do, and AI will create it for you!</p>

                    <div class="ai-settings-row">
                      <div class="form-group">
                        <label for="ai-language">Language <span class="required">*</span></label>
                        <select
                          id="ai-language"
                          v-model="aiLanguage"
                          class="form-select"
                        >
                          <option value="en_US">English (US)</option>
                          <option value="ar">Arabic</option>
                        </select>
                      </div>

                      <div class="form-group">
                        <label for="ai-category">Category <span class="required">*</span></label>
                        <select
                          id="ai-category"
                          v-model="aiCategory"
                          class="form-select"
                        >
                          <option value="UTILITY">Utility</option>
                          <option value="MARKETING">Marketing</option>
                          <option value="AUTHENTICATION">Authentication</option>
                        </select>
                      </div>
                    </div>

                    <div class="form-group">
                      <label for="ai-prompt">What should this template do?</label>
                      <textarea
                        id="ai-prompt"
                        v-model="aiPrompt"
                        class="form-textarea"
                        placeholder="Example: Send order confirmation with order number and delivery date"
                        rows="3"
                        maxlength="500"
                      ></textarea>
                      <p class="field-hint">{{ aiPrompt.length }}/500 characters</p>
                    </div>

                    <button
                      type="button"
                      @click="generateWithAI"
                      class="ai-generate-btn"
                      :disabled="aiGenerating || aiPrompt.length < 10 || !aiLanguage || !aiCategory"
                    >
                      <div v-if="aiGenerating" class="loading-spinner-small"></div>
                      <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 9l1.25-2.75L23 5l-2.75-1.25L19 1l-1.25 2.75L15 5l2.75 1.25L19 9zm-7.5.5L9 4 6.5 9.5 1 12l5.5 2.5L9 20l2.5-5.5L17 12l-5.5-2.5zM19 15l-1.25 2.75L15 19l2.75 1.25L19 23l1.25-2.75L23 19l-2.75-1.25L19 15z"/>
                      </svg>
                      <span>{{ aiGenerating ? 'Generating...' : 'Generate with AI' }}</span>
                    </button>

                    <p v-if="aiError" class="ai-error-message">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                      </svg>
                      {{ aiError }}
                    </p>
                  </div>

                  <div v-else class="ai-success-box">
                    <div class="ai-success-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style="color: #22c55e;">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                      <span>Template generated successfully!</span>
                    </div>
                    <p class="ai-success-message">Your template has been created and populated below. Refine your prompt and regenerate, or customize the template manually.</p>

                    <!-- Keep the prompt visible for refinement -->
                    <div class="form-group">
                      <label for="ai-prompt-refine">Refine your prompt to regenerate:</label>
                      <textarea
                        id="ai-prompt-refine"
                        v-model="aiPrompt"
                        class="form-textarea"
                        placeholder="Example: Send order confirmation with order number and delivery date"
                        rows="3"
                        maxlength="500"
                      ></textarea>
                      <p class="field-hint">{{ aiPrompt.length }}/500 characters</p>
                    </div>

                    <div class="ai-action-buttons">
                      <button
                        type="button"
                        @click="generateWithAI"
                        class="ai-regenerate-btn"
                        :disabled="aiGenerating || aiPrompt.length < 10"
                      >
                        <div v-if="aiGenerating" class="loading-spinner-small"></div>
                        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z"/>
                        </svg>
                        <span>{{ aiGenerating ? 'Regenerating...' : 'Regenerate' }}</span>
                      </button>

                      <button type="button" @click="resetAI" class="ai-reset-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                        </svg>
                        Start Over
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Basic Info -->
                <div class="form-section">
                  <h4 class="section-title">Basic Information</h4>

              <div class="form-group">
                <label for="template-name">Template Name <span class="required">*</span></label>
                <input
                  id="template-name"
                  v-model="templateForm.name"
                  type="text"
                  placeholder="e.g., welcome_message"
                  class="form-input"
                  :disabled="isEditMode"
                  required
                />
                <p class="field-hint">Use lowercase letters, numbers, and underscores only</p>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label for="template-language">Language <span class="required">*</span></label>
                  <select
                    id="template-language"
                    v-model="templateForm.language"
                    class="form-select"
                    :disabled="isEditMode"
                    required
                  >
                    <option value="">Select language</option>
                    <option value="ar">Arabic</option>
                    <option value="en_US">English (US)</option>
                  </select>
                </div>

                <div class="form-group">
                  <label for="template-category">Category <span class="required">*</span></label>
                  <select
                    id="template-category"
                    v-model="templateForm.category"
                    class="form-select"
                    required
                  >
                    <option value="">Select category</option>
                    <option value="MARKETING">Marketing</option>
                    <option value="UTILITY">Utility</option>
                    <option value="AUTHENTICATION">Authentication</option>
                  </select>
                  <p class="field-hint">{{ getCategoryHint() }}</p>
                </div>
              </div>
            </div>

            <!-- Components -->
            <div class="form-section">
              <div class="section-header">
                <h4 class="section-title">Template Components</h4>
              </div>

              <!-- Header Component (Optional) -->
              <div class="component-card">
                <div class="component-header">
                  <label class="component-toggle">
                    <input
                      type="checkbox"
                      v-model="templateForm.hasHeader"
                      @change="toggleHeader"
                    />
                    <span>Header (Optional)</span>
                  </label>
                </div>

                <div v-if="templateForm.hasHeader" class="component-body">
                  <div class="form-group">
                    <label>Header Type</label>
                    <select v-model="templateForm.headerFormat" class="form-select">
                      <option value="TEXT">Text</option>
                      <option value="IMAGE">Image</option>
                      <option value="VIDEO">Video</option>
                      <option value="DOCUMENT">Document</option>
                    </select>
                  </div>

                  <div v-if="templateForm.headerFormat === 'TEXT'" class="form-group">
                    <label>Header Text</label>
                    <input
                      v-model="templateForm.headerText"
                      type="text"
                      placeholder="Header text"
                      class="form-input"
                      maxlength="60"
                    />
                    <p class="field-hint">Max 60 characters</p>
                  </div>
                </div>
              </div>

              <!-- Body Component (Required) -->
              <div class="component-card required-component">
                <div class="component-header">
                  <span class="component-title">Body <span class="required">*</span></span>
                </div>

                <div class="component-body">
                  <div class="form-group">
                    <label>Message Body</label>
                    <textarea
                      v-model="templateForm.bodyText"
                      class="form-textarea"
                      placeholder="Your message here. Use {{1}}, {{2}}, etc. for variables"
                      rows="5"
                      maxlength="1024"
                      required
                    ></textarea>
                    <p class="field-hint">
                      Use {{1}}, {{2}}, etc. for dynamic variables. Max 1024 characters.
                    </p>
                  </div>
                </div>
              </div>

              <!-- Footer Component (Optional) -->
              <div class="component-card">
                <div class="component-header">
                  <label class="component-toggle">
                    <input
                      type="checkbox"
                      v-model="templateForm.hasFooter"
                      @change="toggleFooter"
                    />
                    <span>Footer (Optional)</span>
                  </label>
                </div>

                <div v-if="templateForm.hasFooter" class="component-body">
                  <div class="form-group">
                    <label>Footer Text</label>
                    <input
                      v-model="templateForm.footerText"
                      type="text"
                      placeholder="Footer text"
                      class="form-input"
                      maxlength="60"
                    />
                    <p class="field-hint">Max 60 characters</p>
                  </div>
                </div>
              </div>

              <!-- Buttons Component (Optional) -->
              <div class="component-card">
                <div class="component-header">
                  <label class="component-toggle">
                    <input
                      type="checkbox"
                      v-model="templateForm.hasButtons"
                      @change="toggleButtons"
                    />
                    <span>Buttons (Optional)</span>
                  </label>
                </div>

                <div v-if="templateForm.hasButtons" class="component-body">
                  <div v-for="(button, index) in templateForm.buttons" :key="index" class="button-item">
                    <div class="button-item-header">
                      <span>Button {{ index + 1 }}</span>
                      <button
                        type="button"
                        @click="removeButton(index)"
                        class="remove-btn"
                        v-if="templateForm.buttons.length > 1"
                      >
                        Remove
                      </button>
                    </div>

                    <div class="form-row">
                      <div class="form-group">
                        <label>Button Type</label>
                        <select v-model="button.type" class="form-select">
                          <option value="QUICK_REPLY">Quick Reply</option>
                          <option value="URL">URL</option>
                          <option value="PHONE_NUMBER">Phone Number</option>
                        </select>
                      </div>

                      <div class="form-group">
                        <label>Button Text</label>
                        <input
                          v-model="button.text"
                          type="text"
                          placeholder="Button label"
                          class="form-input"
                          maxlength="25"
                        />
                      </div>
                    </div>

                    <div v-if="button.type === 'URL'" class="form-group">
                      <label>URL</label>
                      <input
                        v-model="button.url"
                        type="url"
                        placeholder="https://example.com"
                        class="form-input"
                      />
                    </div>

                    <div v-if="button.type === 'PHONE_NUMBER'" class="form-group">
                      <label>Phone Number</label>
                      <input
                        v-model="button.phone_number"
                        type="tel"
                        placeholder="+1234567890"
                        class="form-input"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    @click="addButton"
                    class="add-button-btn"
                    v-if="templateForm.buttons.length < 3"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                    </svg>
                    Add Button
                  </button>
                </div>
              </div>
                </div>
              </div>

              <!-- Right Side: Live Preview -->
              <div class="preview-sidebar">
                <div class="preview-sticky">
                  <h4 class="preview-title">Live Preview</h4>
                  <div class="template-preview-box">
                    <div class="whatsapp-bubble">
                      <div v-if="templateForm.hasHeader && templateForm.headerText && templateForm.headerFormat === 'TEXT'" class="preview-header">
                        <strong>{{ templateForm.headerText }}</strong>
                      </div>
                      <div v-if="templateForm.hasHeader && templateForm.headerFormat === 'IMAGE'" class="preview-media">
                        <img src="https://api.quali-d.com/image_placeholder.jpg" alt="Image" class="media-image-preview" />
                      </div>
                      <div v-else-if="templateForm.hasHeader && templateForm.headerFormat === 'VIDEO'" class="preview-media">
                        <img src="https://api.quali-d.com/video_placeholder.jpg" alt="Video" class="media-image-preview" />
                      </div>
                      <div v-else-if="templateForm.hasHeader && templateForm.headerFormat === 'DOCUMENT'" class="preview-media">
                        <div class="document-placeholder">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" style="color: #667781;">
                            <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                          </svg>
                          <span>Document</span>
                        </div>
                      </div>
                      <div class="preview-body">
                        {{ templateForm.bodyText || 'Your message will appear here...' }}
                      </div>
                      <div v-if="templateForm.hasFooter && templateForm.footerText" class="preview-footer">
                        {{ templateForm.footerText }}
                      </div>
                      <div v-if="templateForm.hasButtons && templateForm.buttons.length > 0" class="preview-buttons-container">
                        <button
                          v-for="(btn, idx) in templateForm.buttons"
                          :key="idx"
                          class="preview-btn"
                          type="button"
                        >
                          {{ btn.text || `Button ${idx + 1}` }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Form Actions -->
            <div class="form-actions">
              <button type="button" @click="closeTemplateModal" class="cancel-btn">
                Cancel
              </button>
              <button type="submit" class="submit-btn" :disabled="submitting">
                <div v-if="submitting" class="loading-spinner-small"></div>
                <span v-else>{{ isEditMode ? 'Update Template' : 'Create Template' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Notification Toast -->
    <div v-if="notification" class="notification-toast" :class="notification.type">
      <div class="notification-content">
        <svg v-if="notification.type === 'success'" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
        <div class="notification-text">
          <strong>{{ notification.title }}</strong>
          <p>{{ notification.message }}</p>
        </div>
      </div>
      <button @click="dismissNotification" class="notification-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { AuthService } from '../services/auth'

export default {
  name: 'WhatsAppTemplatesView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      templates: [],
      loading: false,
      syncing: false,
      searchQuery: '',
      searchTimeout: null,
      filters: {
        status: '',
        category: '',
        language: ''
      },
      selectedTemplate: null,
      showDetailModal: false,
      showDeleteModal: false,
      templateToDelete: null,
      deleteConfirmName: '',
      deleting: false,
      notification: null,
      showTemplateModal: false,
      isEditMode: false,
      submitting: false,
      templateForm: {
        name: '',
        language: '',
        category: '',
        hasHeader: false,
        headerFormat: 'TEXT',
        headerText: '',
        bodyText: '',
        hasFooter: false,
        footerText: '',
        hasButtons: false,
        buttons: []
      },
      aiPrompt: '',
      aiLanguage: 'en_US',
      aiCategory: 'UTILITY',
      aiGenerating: false,
      aiGenerated: false,
      aiError: null
    }
  },
  async mounted () {
    await this.loadTemplates()
  },
  methods: {
    async loadTemplates () {
      try {
        this.loading = true
        const token = AuthService.getAuthToken()

        const params = new URLSearchParams()
        if (this.filters.status) params.append('status', this.filters.status)
        if (this.filters.category) params.append('category', this.filters.category)
        if (this.filters.language) params.append('language', this.filters.language)
        if (this.searchQuery) params.append('search', this.searchQuery)

        const response = await fetch(`https://api.quali-d.com/whatsapp/templates?${params.toString()}`, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        console.log('Templates response:', data)

        if (data.success) {
          this.templates = data.templates || []
        } else {
          throw new Error(data.message || 'Failed to load templates')
        }
      } catch (error) {
        console.error('Error loading templates:', error)
        this.showNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to load templates. Please try again.'
        })
        this.templates = []
      } finally {
        this.loading = false
      }
    },

    handleSearch () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadTemplates()
      }, 500)
    },

    async syncTemplates () {
      try {
        this.syncing = true
        const token = AuthService.getAuthToken()

        const response = await fetch('https://api.quali-d.com/whatsapp/templates/sync', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          }
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        console.log('Sync response:', data)

        if (data.success) {
          this.showNotification({
            type: 'success',
            title: 'Sync Complete',
            message: `Successfully synced ${data.synced_count || 0} templates`
          })
          await this.loadTemplates()
        } else {
          throw new Error(data.message || 'Failed to sync templates')
        }
      } catch (error) {
        console.error('Error syncing templates:', error)
        this.showNotification({
          type: 'error',
          title: 'Sync Failed',
          message: 'Failed to sync templates. Please try again.'
        })
      } finally {
        this.syncing = false
      }
    },

    openCreateModal () {
      this.isEditMode = false
      this.resetTemplateForm()
      this.showTemplateModal = true
    },

    closeTemplateModal () {
      this.showTemplateModal = false
      this.resetTemplateForm()
    },

    resetTemplateForm () {
      this.templateForm = {
        name: '',
        language: '',
        category: '',
        hasHeader: false,
        headerFormat: 'TEXT',
        headerText: '',
        bodyText: '',
        hasFooter: false,
        footerText: '',
        hasButtons: false,
        buttons: []
      }
      this.aiPrompt = ''
      this.aiLanguage = 'en_US'
      this.aiCategory = 'UTILITY'
      this.aiGenerating = false
      this.aiGenerated = false
      this.aiError = null
    },

    async generateWithAI () {
      if (this.aiPrompt.length < 10) {
        this.aiError = 'Please provide a more detailed description (at least 10 characters)'
        return
      }

      if (!this.aiLanguage || !this.aiCategory) {
        this.aiError = 'Please select language and category'
        return
      }

      try {
        this.aiGenerating = true
        this.aiError = null

        const token = AuthService.getAuthToken()

        const response = await fetch('https://api.quali-d.com/whatsapp/templates/ai-generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            prompt: this.aiPrompt,
            category: this.aiCategory,
            language: this.aiLanguage
          })
        })

        const data = await response.json()

        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to generate template')
        }

        // Populate the form with AI-generated template
        const template = data.template

        // Force reactivity by creating a new object
        this.templateForm = {
          name: template.name || '',
          language: this.aiLanguage,
          category: this.aiCategory,
          hasHeader: template.hasHeader || false,
          headerFormat: template.headerFormat || 'TEXT',
          headerText: template.headerText || '',
          bodyText: template.bodyText || '',
          hasFooter: template.hasFooter || false,
          footerText: template.footerText || '',
          hasButtons: template.hasButtons || false,
          buttons: template.buttons ? [...template.buttons] : []
        }

        this.aiGenerated = true

        this.showNotification({
          type: 'success',
          title: 'Template Generated',
          message: `AI generated your template! ${data.credits_used} credits used. ${data.remaining_credits} remaining.`
        })
      } catch (error) {
        console.error('Error generating template:', error)
        this.aiError = error.message || 'Failed to generate template. Please try again.'
        this.showNotification({
          type: 'error',
          title: 'Generation Failed',
          message: error.message || 'Failed to generate template with AI.'
        })
      } finally {
        this.aiGenerating = false
      }
    },

    resetAI () {
      // Clear everything to start fresh
      this.aiPrompt = ''
      this.aiGenerated = false
      this.aiError = null
      // Reset all form fields
      this.templateForm.name = ''
      this.templateForm.hasHeader = false
      this.templateForm.headerFormat = 'TEXT'
      this.templateForm.headerText = ''
      this.templateForm.bodyText = ''
      this.templateForm.hasFooter = false
      this.templateForm.footerText = ''
      this.templateForm.hasButtons = false
      this.templateForm.buttons = []
    },

    viewTemplate (template) {
      this.selectedTemplate = template
      this.showDetailModal = true
    },

    closeDetailModal () {
      this.showDetailModal = false
      this.selectedTemplate = null
    },

    editTemplate (template) {
      this.isEditMode = true
      this.selectedTemplate = template
      this.populateFormFromTemplate(template)
      this.showTemplateModal = true
    },

    populateFormFromTemplate (template) {
      this.templateForm.name = template.name
      this.templateForm.language = template.language
      this.templateForm.category = template.category

      const components = template.components || []

      // Find header component
      const headerComponent = components.find(c => c.type === 'HEADER')
      if (headerComponent) {
        this.templateForm.hasHeader = true
        this.templateForm.headerFormat = headerComponent.format || 'TEXT'
        this.templateForm.headerText = headerComponent.text || ''
      }

      // Find body component
      const bodyComponent = components.find(c => c.type === 'BODY')
      if (bodyComponent) {
        this.templateForm.bodyText = bodyComponent.text || ''
      }

      // Find footer component
      const footerComponent = components.find(c => c.type === 'FOOTER')
      if (footerComponent) {
        this.templateForm.hasFooter = true
        this.templateForm.footerText = footerComponent.text || ''
      }

      // Find buttons component
      const buttonsComponent = components.find(c => c.type === 'BUTTONS')
      if (buttonsComponent && buttonsComponent.buttons) {
        this.templateForm.hasButtons = true
        this.templateForm.buttons = buttonsComponent.buttons.map(btn => ({
          type: btn.type,
          text: btn.text,
          url: btn.url || '',
          phone_number: btn.phone_number || ''
        }))
      }
    },

    async submitTemplate () {
      try {
        this.submitting = true

        // Build components array
        const components = []

        // Add header if enabled
        if (this.templateForm.hasHeader) {
          const headerComponent = {
            type: 'HEADER',
            format: this.templateForm.headerFormat
          }
          if (this.templateForm.headerFormat === 'TEXT' && this.templateForm.headerText) {
            headerComponent.text = this.templateForm.headerText
          }
          components.push(headerComponent)
        }

        // Add body (required)
        components.push({
          type: 'BODY',
          text: this.templateForm.bodyText
        })

        // Add footer if enabled
        if (this.templateForm.hasFooter && this.templateForm.footerText) {
          components.push({
            type: 'FOOTER',
            text: this.templateForm.footerText
          })
        }

        // Add buttons if enabled
        if (this.templateForm.hasButtons && this.templateForm.buttons.length > 0) {
          const buttons = this.templateForm.buttons.map(btn => {
            const button = {
              type: btn.type,
              text: btn.text
            }
            if (btn.type === 'URL' && btn.url) {
              button.url = btn.url
            }
            if (btn.type === 'PHONE_NUMBER' && btn.phone_number) {
              button.phone_number = btn.phone_number
            }
            return button
          }).filter(btn => btn.text) // Only include buttons with text

          if (buttons.length > 0) {
            components.push({
              type: 'BUTTONS',
              buttons
            })
          }
        }

        const token = AuthService.getAuthToken()

        if (this.isEditMode) {
          // Update existing template
          const response = await fetch(`https://api.quali-d.com/whatsapp/templates/${this.selectedTemplate.id}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              category: this.templateForm.category,
              components
            })
          })

          const data = await response.json()

          if (!response.ok || !data.success) {
            throw new Error(data.message || 'Failed to update template')
          }

          this.showNotification({
            type: 'success',
            title: 'Template Updated',
            message: `${this.templateForm.name} has been updated successfully`
          })
        } else {
          // Create new template
          const response = await fetch('https://api.quali-d.com/whatsapp/templates', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              name: this.templateForm.name,
              language: this.templateForm.language,
              category: this.templateForm.category,
              components
            })
          })

          const data = await response.json()

          if (!response.ok || !data.success) {
            throw new Error(data.message || 'Failed to create template')
          }

          this.showNotification({
            type: 'success',
            title: 'Template Created',
            message: `${this.templateForm.name} has been submitted for approval`
          })
        }

        this.closeTemplateModal()
        await this.loadTemplates()
      } catch (error) {
        console.error('Error submitting template:', error)
        this.showNotification({
          type: 'error',
          title: this.isEditMode ? 'Update Failed' : 'Creation Failed',
          message: error.message || 'Failed to submit template. Please try again.'
        })
      } finally {
        this.submitting = false
      }
    },

    toggleHeader () {
      if (!this.templateForm.hasHeader) {
        this.templateForm.headerFormat = 'TEXT'
        this.templateForm.headerText = ''
      }
    },

    toggleFooter () {
      if (!this.templateForm.hasFooter) {
        this.templateForm.footerText = ''
      }
    },

    toggleButtons () {
      if (this.templateForm.hasButtons && this.templateForm.buttons.length === 0) {
        this.addButton()
      } else if (!this.templateForm.hasButtons) {
        this.templateForm.buttons = []
      }
    },

    addButton () {
      if (this.templateForm.buttons.length < 3) {
        this.templateForm.buttons.push({
          type: 'QUICK_REPLY',
          text: '',
          url: '',
          phone_number: ''
        })
      }
    },

    removeButton (index) {
      this.templateForm.buttons.splice(index, 1)
    },

    getCategoryHint () {
      const hints = {
        MARKETING: 'For promotional content and offers',
        UTILITY: 'For transactional messages and updates',
        AUTHENTICATION: 'For OTP and verification codes'
      }
      return hints[this.templateForm.category] || 'Select a category'
    },

    openDeleteModal (template) {
      this.templateToDelete = template
      this.deleteConfirmName = ''
      this.showDeleteModal = true
    },

    closeDeleteModal () {
      this.showDeleteModal = false
      this.templateToDelete = null
      this.deleteConfirmName = ''
    },

    async confirmDelete () {
      if (this.deleteConfirmName !== this.templateToDelete?.name) return

      try {
        this.deleting = true
        const token = AuthService.getAuthToken()

        const response = await fetch(`https://api.quali-d.com/whatsapp/templates/${this.templateToDelete.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            name: this.templateToDelete.name
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const data = await response.json()
        console.log('Delete response:', data)

        if (data.success) {
          this.showNotification({
            type: 'success',
            title: 'Template Deleted',
            message: `${this.templateToDelete.name} has been deleted successfully`
          })
          this.closeDeleteModal()
          await this.loadTemplates()
        } else {
          throw new Error(data.message || 'Failed to delete template')
        }
      } catch (error) {
        console.error('Error deleting template:', error)
        this.showNotification({
          type: 'error',
          title: 'Delete Failed',
          message: error.message || 'Failed to delete template. Please try again.'
        })
      } finally {
        this.deleting = false
      }
    },

    getTemplateBodyPreview (template) {
      const bodyComponent = template.components?.find(c => c.type === 'BODY')
      if (!bodyComponent) return 'No preview available'

      let text = bodyComponent.text || ''
      // Replace variables with placeholder
      text = text.replace(/\{\{(\d+)\}\}/g, '[Variable]')

      // Truncate if too long
      if (text.length > 100) {
        return text.substring(0, 100) + '...'
      }
      return text
    },

    showNotification (notification) {
      this.notification = notification
      setTimeout(() => {
        this.dismissNotification()
      }, 5000)
    },

    dismissNotification () {
      this.notification = null
    },

    highlightVariables (text) {
      if (!text) return ''
      // Replace {{1}}, {{2}}, etc. with highlighted spans
      return text.replace(/\{\{(\d+)\}\}/g, '<span class="variable-highlight">{{$1}}</span>')
    },

    getLanguageName (code) {
      const languageMap = {
        en: 'English',
        en_US: 'English',
        en_GB: 'English',
        ar: 'Arabic'
      }
      return languageMap[code] || code
    },

    formatTemplateTime () {
      const now = new Date()
      return now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }
  }
}
</script>

<style scoped>
.templates-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary);
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-left: 260px;
  transition: margin-left 0.3s ease;
}

.page-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.templates-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;
}

.header-left {
  flex: 1;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 12px;
}

.sync-btn, .create-btn {
  height: 42px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.sync-btn {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
}

.sync-btn:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.sync-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.create-btn {
  background: #25D366;
  color: white;
}

.create-btn:hover {
  background: #20c05a;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

/* Filters Section */
.filters-section {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 300px;
  position: relative;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-tertiary);
}

.search-input {
  width: 100%;
  height: 42px;
  padding: 0 16px 0 48px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.filter-controls {
  display: flex;
  gap: 12px;
}

.filter-select {
  height: 42px;
  padding: 0 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-select:hover {
  border-color: var(--border-secondary);
}

.filter-select:focus {
  outline: none;
  border-color: #25D366;
}

/* Loading and Empty States */
.loading-state, .empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  gap: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(37, 211, 102, 0.2);
  border-top: 3px solid #25D366;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 8px;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.empty-state p {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 24px 0;
}

.create-btn-empty {
  height: 42px;
  padding: 0 24px;
  border: none;
  border-radius: 8px;
  background: #25D366;
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.create-btn-empty:hover {
  background: #20c05a;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

/* Templates Grid */
.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.template-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.template-card:hover {
  border-color: var(--border-secondary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

/* Template Info Header */
.template-info-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.template-name-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.template-language {
  font-size: 12px;
  color: var(--text-tertiary);
  font-weight: 500;
}

/* WhatsApp Message Bubble */
.whatsapp-message-bubble {
  background: #D9FDD3;
  border-radius: 8px;
  padding: 8px 12px;
  position: relative;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
  margin-bottom: 12px;
}

.message-component {
  margin-bottom: 8px;
}

.message-component:last-child {
  margin-bottom: 0;
}

.message-header {
  margin-bottom: 8px;
}

.message-header strong {
  font-size: 15px;
  color: #1F2937;
  line-height: 1.4;
  display: block;
}

.message-media {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 6px;
  font-size: 13px;
  color: #667781;
  font-weight: 500;
}

.message-body {
  font-size: 14px;
  color: #1F2937;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.message-footer {
  font-size: 12px;
  color: #667781;
  margin-top: 6px;
  font-style: italic;
}

.message-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.message-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  color: #0088CC;
  font-size: 13px;
  font-weight: 500;
  cursor: default;
  transition: background 0.2s ease;
}

.message-button:hover {
  background: rgba(255, 255, 255, 0.7);
}

.message-timestamp {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 4px;
  font-size: 11px;
  color: #667781;
}

.message-timestamp svg {
  color: #53BDEB;
  flex-shrink: 0;
}

/* Variable Highlighting */
.variable-highlight {
  display: inline-block;
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 13px;
  border: 1px solid rgba(59, 130, 246, 0.3);
  font-family: 'Courier New', monospace;
}

/* Template Status Info */
.template-status-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 12px;
}

/* Quality Rating Badge on Template Cards */
.quality-rating-badge {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border-radius: 10px;
  border-left: 4px solid;
  transition: all 0.2s ease;
}

.quality-rating-badge.quality-green {
  background: rgba(34, 197, 94, 0.08);
  border-left-color: #22c55e;
}

.quality-rating-badge.quality-yellow {
  background: rgba(251, 191, 36, 0.08);
  border-left-color: #fbbf24;
}

.quality-rating-badge.quality-red {
  background: rgba(239, 68, 68, 0.08);
  border-left-color: #ef4444;
}

.quality-rating-badge.quality-blue {
  background: rgba(59, 130, 246, 0.08);
  border-left-color: #3b82f6;
}

.quality-rating-badge.quality-gray {
  background: rgba(107, 114, 128, 0.08);
  border-left-color: #6b7280;
}

.quality-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.quality-green .quality-icon {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.quality-yellow .quality-icon {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
}

.quality-red .quality-icon {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.quality-blue .quality-icon {
  background: rgba(59, 130, 246, 0.15);
  color: #3b82f6;
}

.quality-gray .quality-icon {
  background: rgba(107, 114, 128, 0.15);
  color: #6b7280;
}

.quality-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.quality-label {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.quality-green .quality-label {
  color: #16a34a;
}

.quality-yellow .quality-label {
  color: #d97706;
}

.quality-red .quality-label {
  color: #dc2626;
}

.quality-blue .quality-label {
  color: #2563eb;
}

.quality-gray .quality-label {
  color: #4b5563;
}

.quality-description {
  font-size: 12px;
  line-height: 1.4;
  color: var(--text-secondary);
}

/* Rejection Info */
.rejection-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  background: rgba(239, 68, 68, 0.08);
  border-radius: 10px;
  border-left: 4px solid #ef4444;
}

.rejection-info svg {
  color: #ef4444;
  flex-shrink: 0;
  margin-top: 2px;
}

.rejection-info span {
  color: #dc2626;
  line-height: 1.5;
  font-size: 13px;
}

.template-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.template-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  word-break: break-word;
}

.template-badges {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
}

.status-badge, .category-badge, .language-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
}

.status-badge.status-approved {
  background: rgba(34, 197, 94, 0.1);
  color: #22c55e;
  border: 1px solid rgba(34, 197, 94, 0.2);
}

.status-badge.status-pending {
  background: rgba(251, 191, 36, 0.1);
  color: #fbbf24;
  border: 1px solid rgba(251, 191, 36, 0.2);
}

.status-badge.status-rejected {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.status-badge.status-paused {
  background: rgba(249, 115, 22, 0.1);
  color: #f97316;
  border: 1px solid rgba(249, 115, 22, 0.2);
}

.status-badge.status-disabled {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
  border: 1px solid rgba(107, 114, 128, 0.2);
}

.category-badge.category-marketing {
  background: rgba(147, 51, 234, 0.1);
  color: #9333ea;
  border: 1px solid rgba(147, 51, 234, 0.2);
}

.category-badge.category-utility {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.2);
}

.category-badge.category-authentication {
  background: rgba(236, 72, 153, 0.1);
  color: #ec4899;
  border: 1px solid rgba(236, 72, 153, 0.2);
}

.template-body {
  flex: 1;
}

.template-preview {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.template-meta {
  display: flex;
  gap: 16px;
  align-items: center;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-tertiary);
}

.meta-item svg {
  flex-shrink: 0;
}

.quality-score {
  font-weight: 600;
}

.quality-score.quality-high {
  color: #22c55e;
}

.quality-score.quality-medium {
  color: #fbbf24;
}

.quality-score.quality-low {
  color: #ef4444;
}

.rejection-reason {
  display: flex;
  gap: 8px;
  padding: 12px;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.1);
  border-radius: 8px;
  font-size: 12px;
  color: #dc2626;
  line-height: 1.4;
}

.rejection-reason svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.template-actions {
  display: flex;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--border-primary);
}

.action-btn {
  flex: 1;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 1px solid var(--border-primary);
  background: var(--bg-primary);
  color: var(--text-secondary);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 13px;
  font-weight: 500;
}

.action-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.view-btn:hover {
  color: #3b82f6;
  border-color: #3b82f6;
}

.edit-btn:hover {
  color: #22c55e;
  border-color: #22c55e;
}

.delete-btn:hover {
  color: #ef4444;
  border-color: #ef4444;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.modal-container {
  background: var(--bg-primary);
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--text-tertiary);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.modal-close-btn:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

/* Template Detail Modal */
.template-detail-modal {
  max-width: 700px;
}

.template-detail-header {
  margin-bottom: 24px;
}

.template-detail-name {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 12px 0;
}

.template-detail-badges {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.language-badge {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
  border: 1px solid rgba(107, 114, 128, 0.2);
}

.template-preview-section {
  margin-bottom: 24px;
}

.template-preview-section h4 {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.whatsapp-preview {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
}

.preview-component {
  margin-bottom: 12px;
}

.preview-component:last-child {
  margin-bottom: 0;
}

.preview-header {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
}

.preview-media {
  padding: 12px;
  background: var(--bg-tertiary);
  border-radius: 8px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
}

.preview-body {
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.6;
  white-space: pre-wrap;
}

.preview-footer {
  font-size: 12px;
  color: var(--text-tertiary);
  margin-top: 8px;
}

.preview-buttons {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.preview-button {
  padding: 10px;
  background: var(--bg-primary);
  border: 1px solid #25D366;
  color: #25D366;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: default;
}

.quality-section, .rejection-section {
  margin-top: 24px;
}

.quality-section h4, .rejection-section h4 {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Quality Detail Card in Modal */
.quality-detail-card {
  padding: 20px;
  border-radius: 12px;
  border: 2px solid;
  transition: all 0.3s ease;
}

.quality-detail-card.quality-green {
  background: rgba(34, 197, 94, 0.05);
  border-color: rgba(34, 197, 94, 0.3);
}

.quality-detail-card.quality-yellow {
  background: rgba(251, 191, 36, 0.05);
  border-color: rgba(251, 191, 36, 0.3);
}

.quality-detail-card.quality-red {
  background: rgba(239, 68, 68, 0.05);
  border-color: rgba(239, 68, 68, 0.3);
}

.quality-detail-card.quality-blue {
  background: rgba(59, 130, 246, 0.05);
  border-color: rgba(59, 130, 246, 0.3);
}

.quality-detail-card.quality-gray {
  background: rgba(107, 114, 128, 0.05);
  border-color: rgba(107, 114, 128, 0.3);
}

.quality-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.quality-icon-large {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
}

.quality-green .quality-icon-large {
  background: rgba(34, 197, 94, 0.15);
  color: #22c55e;
}

.quality-yellow .quality-icon-large {
  background: rgba(251, 191, 36, 0.15);
  color: #fbbf24;
}

.quality-red .quality-icon-large {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.quality-blue .quality-icon-large {
  background: rgba(59, 130, 246, 0.15);
  color: #3b82f6;
}

.quality-gray .quality-icon-large {
  background: rgba(107, 114, 128, 0.15);
  color: #6b7280;
}

.quality-info-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.quality-rating-label {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.quality-green .quality-rating-label {
  color: #16a34a;
}

.quality-yellow .quality-rating-label {
  color: #d97706;
}

.quality-red .quality-rating-label {
  color: #dc2626;
}

.quality-blue .quality-rating-label {
  color: #2563eb;
}

.quality-gray .quality-rating-label {
  color: #4b5563;
}

.quality-rating-value {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  opacity: 0.7;
}

.quality-description-text {
  font-size: 14px;
  line-height: 1.6;
  color: var(--text-secondary);
  margin: 0;
}

.quality-date {
  font-size: 12px;
  color: var(--text-tertiary);
}

.rejection-box {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.1);
  border-radius: 8px;
  color: #dc2626;
  line-height: 1.5;
}

.rejection-box svg {
  flex-shrink: 0;
  margin-top: 2px;
}

/* Delete Modal */
.delete-modal {
  max-width: 500px;
}

.warning-message {
  text-align: center;
  margin-bottom: 24px;
}

.warning-message svg {
  color: #fbbf24;
  margin-bottom: 16px;
}

.warning-message p {
  font-size: 16px;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.warning-subtitle {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

.delete-form {
  margin-bottom: 24px;
}

.delete-form label {
  display: block;
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  font-weight: 500;
}

.delete-input {
  width: 100%;
  height: 42px;
  padding: 0 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s ease;
}

.delete-input:focus {
  outline: none;
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.cancel-btn, .delete-confirm-btn {
  height: 42px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-primary);
}

.cancel-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
}

.delete-confirm-btn {
  background: #ef4444;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.delete-confirm-btn:hover:not(:disabled) {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.delete-confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Notification Toast */
.notification-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1200;
  min-width: 350px;
  max-width: 500px;
  background: var(--bg-secondary);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--border-primary);
  animation: slideInRight 0.3s ease;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.notification-toast.success {
  border-left: 4px solid #22c55e;
}

.notification-toast.error {
  border-left: 4px solid #ef4444;
}

.notification-toast.info {
  border-left: 4px solid #3b82f6;
}

.notification-content {
  flex: 1;
  display: flex;
  gap: 12px;
}

.notification-content svg {
  flex-shrink: 0;
  margin-top: 2px;
}

.notification-toast.success svg {
  color: #22c55e;
}

.notification-toast.error svg {
  color: #ef4444;
}

.notification-toast.info svg {
  color: #3b82f6;
}

.notification-text strong {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.notification-text p {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0;
  line-height: 1.4;
}

.notification-close {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: var(--text-tertiary);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.notification-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Template Builder Modal */
.template-builder-modal {
  max-width: 1200px;
}

.template-form {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.form-layout {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 24px;
  margin-bottom: 24px;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  max-height: calc(90vh - 200px);
  padding-right: 12px;
}

.form-fields::-webkit-scrollbar {
  width: 6px;
}

.form-fields::-webkit-scrollbar-track {
  background: var(--bg-tertiary);
  border-radius: 3px;
}

.form-fields::-webkit-scrollbar-thumb {
  background: var(--border-secondary);
  border-radius: 3px;
}

.form-fields::-webkit-scrollbar-thumb:hover {
  background: var(--text-tertiary);
}

.preview-sidebar {
  position: relative;
}

.preview-sticky {
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-primary);
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--border-primary);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary);
}

.required {
  color: #ef4444;
}

.form-input, .form-select, .form-textarea {
  height: 42px;
  padding: 0 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-size: 14px;
  transition: all 0.2s ease;
  font-family: inherit;
}

.form-textarea {
  height: auto;
  padding: 12px 16px;
  resize: vertical;
  line-height: 1.5;
}

.form-input:focus, .form-select:focus, .form-textarea:focus {
  outline: none;
  border-color: #25D366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.1);
}

.form-input:disabled, .form-select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--bg-tertiary);
}

.field-hint {
  font-size: 12px;
  color: var(--text-tertiary);
  margin: 0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* Component Cards */
.component-card {
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  overflow: hidden;
  background: var(--bg-secondary);
}

.component-card.required-component {
  border-color: rgba(37, 211, 102, 0.3);
  background: rgba(37, 211, 102, 0.02);
}

.component-header {
  padding: 12px 16px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-primary);
}

.component-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.component-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.component-toggle input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #25D366;
}

.component-toggle span {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.component-body {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Button Items */
.button-item {
  padding: 16px;
  background: var(--bg-tertiary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.button-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.button-item-header span {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
}

.remove-btn {
  padding: 6px 12px;
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: #ef4444;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  border-color: #ef4444;
}

.add-button-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: 1px dashed var(--border-primary);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-button-btn:hover {
  border-color: #25D366;
  color: #25D366;
  background: rgba(37, 211, 102, 0.05);
}

/* Preview Box */
.template-preview-box {
  padding: 24px;
  background: #ECE5DD;
  border-radius: 8px;
  display: flex;
  justify-content: center;
}

.whatsapp-bubble {
  background: #D9FDD3;
  border-radius: 8px;
  padding: 8px 12px;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
}

.whatsapp-bubble .preview-header {
  font-size: 15px;
  font-weight: 600;
  color: #1F2937;
  margin-bottom: 8px;
}

.whatsapp-bubble .preview-media {
  padding: 12px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 6px;
  text-align: center;
  color: #667781;
  font-size: 13px;
  margin-bottom: 8px;
}

.whatsapp-bubble .preview-body {
  font-size: 14px;
  color: #1F2937;
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.whatsapp-bubble .preview-footer {
  font-size: 12px;
  color: #667781;
  margin-top: 6px;
  font-style: italic;
}

.whatsapp-bubble .preview-buttons-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
}

.whatsapp-bubble .preview-btn {
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  color: #0088CC;
  font-size: 13px;
  font-weight: 500;
  cursor: default;
  text-align: center;
}

/* AI Generation Section */
.ai-section {
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%);
  border: 2px solid rgba(147, 51, 234, 0.2);
  border-radius: 12px;
  padding: 20px;
}

.ai-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.ai-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ai-cost-badge {
  padding: 4px 12px;
  background: rgba(147, 51, 234, 0.1);
  color: #9333ea;
  border: 1px solid rgba(147, 51, 234, 0.3);
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.ai-description {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.ai-settings-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.ai-prompt-box, .ai-success-box {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ai-generate-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #9333ea 0%, #ec4899 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(147, 51, 234, 0.3);
}

.ai-generate-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(147, 51, 234, 0.4);
}

.ai-generate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.ai-error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
  color: #dc2626;
  font-size: 13px;
  margin: 0;
}

.ai-success-box {
  background: rgba(34, 197, 94, 0.05);
  border: 1px solid rgba(34, 197, 94, 0.2);
  border-radius: 8px;
  padding: 16px;
}

.ai-success-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 600;
  color: #22c55e;
  margin-bottom: 8px;
}

.ai-success-message {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 16px 0;
  line-height: 1.5;
}

.ai-action-buttons {
  display: flex;
  gap: 12px;
}

.ai-regenerate-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #9333ea 0%, #ec4899 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ai-regenerate-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(147, 51, 234, 0.3);
}

.ai-regenerate-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.ai-reset-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.ai-reset-btn:hover {
  background: var(--bg-tertiary);
  border-color: var(--border-secondary);
  color: var(--text-primary);
}

.media-image-preview {
  width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: cover;
  border-radius: 6px;
}

.document-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 6px;
}

.document-placeholder span {
  font-size: 13px;
  color: #667781;
  font-weight: 500;
}

/* Form Actions */
.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 16px;
  border-top: 1px solid var(--border-primary);
}

.submit-btn {
  height: 42px;
  padding: 0 24px;
  background: #25D366;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.submit-btn:hover:not(:disabled) {
  background: #20c05a;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }

  .page-header {
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .sync-btn, .create-btn {
    width: 100%;
    justify-content: center;
  }

  .filters-section {
    flex-direction: column;
  }

  .search-box {
    min-width: 100%;
  }

  .filter-controls {
    flex-direction: column;
  }

  .filter-select {
    width: 100%;
  }

  .templates-grid {
    grid-template-columns: 1fr;
  }

  .notification-toast {
    left: 16px;
    right: 16px;
    min-width: auto;
  }

  .template-builder-modal {
    max-width: 100%;
    margin: 0 16px;
  }

  .form-layout {
    grid-template-columns: 1fr;
  }

  .form-fields {
    max-height: 400px;
    padding-right: 0;
  }

  .preview-sidebar {
    order: -1;
  }

  .preview-sticky {
    position: relative;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
